import React, { createContext, useContext, useReducer, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";

// Base URL MUST include /api
const API_URL = import.meta.env.VITE_API_URL;

const AuthContext = createContext();

const initialState = {
  user: null,
  token: localStorage.getItem("hostly_token"),
  isAuthenticated: false,
  loading: true,
  error: null,
};

const authReducer = (state, action) => {
  switch (action.type) {
    case "USER_LOADING":
      return { ...state, loading: true, error: null };

    case "USER_LOADED":
      return {
        ...state,
        isAuthenticated: true,
        loading: false,
        user: action.payload,
        error: null,
      };

    case "LOGIN_SUCCESS":
    case "REGISTER_SUCCESS":
      localStorage.setItem("hostly_token", action.payload.token);
      return {
        ...state,
        ...action.payload,
        isAuthenticated: true,
        loading: false,
        error: null,
      };

    case "AUTH_ERROR":
    case "LOGIN_FAIL":
    case "REGISTER_FAIL":
    case "LOGOUT":
      localStorage.removeItem("hostly_token");
      return {
        ...state,
        token: null,
        isAuthenticated: false,
        loading: false,
        user: null,
        error: action.payload,
      };

    case "CLEAR_ERRORS":
      return { ...state, error: null };

    case "UPDATE_USER":
      return { ...state, user: { ...state.user, ...action.payload } };

    default:
      return state;
  }
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Configure axios ONCE
  useEffect(() => {
    axios.defaults.baseURL = API_URL;

    if (state.token) {
      axios.defaults.headers.common.Authorization = `Bearer ${state.token}`;
    } else {
      delete axios.defaults.headers.common.Authorization;
    }
  }, [state.token]);

  // Load user
  const loadUser = async () => {
    if (!state.token) {
      dispatch({ type: "AUTH_ERROR" });
      return;
    }

    dispatch({ type: "USER_LOADING" });
    try {
      const res = await axios.get("/auth/me"); // ✅ FIXED

      const userData = {
        ...res.data.user,
        id: res.data.user._id || res.data.user.id,
        _id: res.data.user._id || res.data.user.id,
      };

      dispatch({ type: "USER_LOADED", payload: userData });
    } catch (error) {
      dispatch({
        type: "AUTH_ERROR",
        payload: error.response?.data?.message || "Failed to load user",
      });
    }
  };

  // Register
  const register = async (formData) => {
    dispatch({ type: "USER_LOADING" });
    try {
      const res = await axios.post("/auth/register", formData); // ✅ FIXED

      dispatch({ type: "REGISTER_SUCCESS", payload: res.data });

      toast.success(`🎉 Welcome to Hostly Inn, ${res.data.user.name}!`);
      return { success: true, studentId: res.data.user.studentId };
    } catch (error) {
      const message = error.response?.data?.message || "Registration failed";
      dispatch({ type: "REGISTER_FAIL", payload: message });
      toast.error(message);
      return { success: false, message };
    }
  };

  // Login
  const login = async (email, password) => {
    dispatch({ type: "USER_LOADING" });
    try {
      const res = await axios.post("/auth/login", { email, password }); // ✅ FIXED

      const userData = {
        ...res.data.user,
        id: res.data.user._id || res.data.user.id,
        _id: res.data.user._id || res.data.user.id,
      };

      dispatch({
        type: "LOGIN_SUCCESS",
        payload: { ...res.data, user: userData },
      });

      toast.success(`Welcome back, ${userData.name}! 👋`);
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || "Login failed";
      dispatch({ type: "LOGIN_FAIL", payload: message });
      toast.error(message);
      return { success: false, message };
    }
  };

  // Google Login
  const googleLogin = async (idToken) => {
    dispatch({ type: "USER_LOADING" });
    try {
      const res = await axios.post("/auth/google", { idToken }); // ✅ Send idToken to backend

      const userData = {
        ...res.data.user,
        id: res.data.user._id || res.data.user.id,
        _id: res.data.user._id || res.data.user.id,
      };

      dispatch({
        type: "LOGIN_SUCCESS",
        payload: { ...res.data, user: userData },
      });

      toast.success(`Welcome, ${userData.name}! 🎉`);
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || "Google login failed";
      dispatch({ type: "LOGIN_FAIL", payload: message });
      toast.error(message);
      return { success: false, message };
    }
  };

  const logout = () => {
    dispatch({ type: "LOGOUT" });
    toast.info("Logged out successfully 👋");
  };

  const updateUser = (userData) => {
    dispatch({ type: "UPDATE_USER", payload: userData });
  };

  const clearErrors = () => dispatch({ type: "CLEAR_ERRORS" });

  useEffect(() => {
    loadUser();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        register,
        login,
        googleLogin,
        logout,
        loadUser,
        updateUser,
        clearErrors,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
};
